<script>
  import { router, page } from '@inertiajs/svelte'
  import dayjs from 'dayjs'
  import localizedFormat from 'dayjs/plugin/localizedFormat'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FilterBar from '../../components/filter_bar.svelte'
  import Paginator from '../../components/paginator.svelte'
  import FilterTabs from '../../components/filter_tabs.svelte'
  import SearchInput from '../../components/search_input.svelte'
  import FilterSelect from '../../components/filter_select.svelte'
  import Modal from '../../components/modal.svelte'
  import EntityList from '../../components/entity/entity_list.svelte'
  import DisplayOptions from '../../components/entity/display_options.svelte'
  import CreateExampleModal from '../../components/quests/create_example_modal.svelte'
  import RejectModal from '../../components/quest_submissions/reject_modal.svelte'
  import { Button, Combobox } from '../../components/ui'
  import { confirm } from '../../components/confirm.js'
  import { inertiaAction } from '../../lib/inertia_action.js'

  dayjs.extend(localizedFormat)

  let {
    submissions = [],
    pagy = { page: 1, pages: 1, count: 0 },
    total_count = null,
    mode = 'pending',
    filters = {},
    rejection_categories = [],
    rating_options = [],
    curation_levels = [],
    empty_message = null,
    quest_occurrences = { quests: [], eligible_places: [] }
  } = $props()

  let viewMode = $state(null)

  const MEDIA_TYPE_OPTIONS = [
    { value: 'photo', label: 'Photo' },
    { value: 'video', label: 'Video' }
  ]
  const SORT_OPTIONS = [
    { value: 'oldest', label: 'Oldest first' },
    { value: 'newest', label: 'Newest first' },
    { value: 'recently_reviewed', label: 'Recently reviewed' }
  ]
  const sortOptions = $derived(mode === 'pending' ? SORT_OPTIONS.filter((o) => o.value !== 'recently_reviewed') : SORT_OPTIONS)

  const categoryLabel = (value) => rejection_categories.find((c) => c.value === value)?.label

  // -- Filters ------------------------------------------------------------
  // Mutates the current URL (like filter_tabs/search_input do) so switching one
  // filter doesn't clobber the others still in the query string.
  let search = $state(filters.search || '')

  const applyFilters = (changes) => {
    const url = new URL($page.url, 'http://placeholder')
    Object.entries(changes).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value)
      else url.searchParams.delete(key)
    })
    url.searchParams.delete('page')
    router.get(`${url.pathname}${url.search}`, {}, { preserveState: true, preserveScroll: true })
  }

  // -- Selection ------------------------------------------------------------
  let selectedIds = $state(new Set())

  // Any server-driven filter change (tab, search, selects — all full navigations
  // via preserveState) invalidates the current page's selection.
  let lastFilterKey = $state(JSON.stringify({ mode, filters, page: pagy.page }))
  $effect(() => {
    const key = JSON.stringify({ mode, filters, page: pagy.page })
    if (key !== lastFilterKey) {
      lastFilterKey = key
      selectedIds = new Set()
    }
  })

  const toggleSelect = (id) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    selectedIds = next
  }

  const toggleSelectAll = () => {
    const next = new Set(selectedIds)
    const pageIds = submissions.map((s) => s.id)
    const willSelectAll = !pageIds.every((id) => next.has(id))
    pageIds.forEach((id) => (willSelectAll ? next.add(id) : next.delete(id)))
    selectedIds = next
  }

  // -- Review requests + undo ------------------------------------------------------------
  // Every action is an Inertia visit that redirects back here with a flash
  // (the global toast reports the outcome, bulk failures included) and
  // refreshes the list.
  const visit = (method, url, data) => inertiaAction(router, method, url, data)

  // Session-local undo stack, most recent last. An entry only remembers the
  // server action that reverses one landed review or bulk approve; it is popped
  // once the server answers it — only a network failure keeps it for a retry (the
  // flash says why an undo failed). It lives in the header rather than on the row because a
  // reviewed row leaves the filtered tab on reload.
  let undoStack = $state([])
  let undoBusy = $state(false)

  const lastReview = $derived(undoStack[undoStack.length - 1])
  const undoTooltip = $derived(lastReview ? lastReview.label : 'Undo — reverses the last review or bulk approve you make from this page')

  const undoLastReview = async () => {
    const entry = lastReview
    if (!entry || undoBusy) return
    undoBusy = true
    const result = await visit(entry.method, entry.url, entry.data)
    undoBusy = false
    if (result.ok || result.code) undoStack = undoStack.filter((e) => e !== entry)
  }

  const recordUndo = (entry) => (undoStack = [...undoStack, entry])

  let bulkSubmitting = $state(false)

  const bulkApprove = async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return

    const confirmed = await confirm({
      title: `Approve ${ids.length} submission${ids.length === 1 ? '' : 's'}?`,
      body: "Each user is awarded the quest's points or store item reward and notified, exactly like a single approve."
    })
    if (!confirmed) return

    bulkSubmitting = true
    const result = await visit('post', '/quest-submissions/bulk_review', { submission_ids: ids })
    bulkSubmitting = false
    if (!result.ok) return

    // Undoing sends every id; the server only reverses the ones this admin approved.
    recordUndo({
      method: 'post',
      url: '/quest-submissions/bulk_unreview',
      data: { submission_ids: ids },
      label: `Undo last bulk approve — returns up to ${ids.length} submissions to Pending`
    })
    selectedIds = new Set()
  }

  // -- Row actions ------------------------------------------------------------
  // Review opens at the list page this row is on, so the clicked row is in the
  // queue's first batch.
  const openReview = (submission) => {
    const query = { mode, ...filters }
    if (submission) {
      query.start = submission.id
      if (pagy.page > 1) query.page = pagy.page
    }
    const params = new URLSearchParams()
    Object.entries(query).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })
    router.visit(`/quest-submissions/review?${params.toString()}`)
  }

  // `undoable` marks a review decision (approve / reject / delete); curation
  // (rating, Example) is reversed by its own toggle instead.
  const rowAction = async (submission, action, body, { undoable = false } = {}) => {
    const result = await visit('patch', `/quest-submissions/${submission.id}/${action}`, body)
    if (result.ok && undoable) {
      recordUndo({ method: 'patch', url: `/quest-submissions/${submission.id}/unreview`, data: {}, label: 'Undo last review — returns the submission to Pending' })
    }
    return result.ok
  }

  const approve = (submission) => rowAction(submission, 'approve', {}, { undoable: true })

  const approveExample = (submission) => rowAction(submission, 'approve', { example: true }, { undoable: true })

  const deleteMoment = async (submission) => {
    const confirmed = await confirm({
      title: 'Delete Moment (TOS violation)',
      body:
        "This permanently deletes the user's moment. The quest returns to Ready so they can re-submit, and they see " +
        '"Inappropriate content" as the rejection reason. Are you sure?',
      confirmLabel: 'Delete',
      variant: 'error'
    })
    if (!confirmed) return
    rowAction(submission, 'delete_moment', {}, { undoable: true })
  }

  // Pressing the active level clears the rating.
  const rate = (submission, level) =>
    rowAction(submission, 'rate', { level: submission.curation_level === level ? 'none' : level })

  const markExample = (submission) => rowAction(submission, 'mark_example', { example: true })

  let rejectOpen = $state(false)
  let rejectTarget = $state(null)

  const openReject = (submission) => {
    rejectTarget = submission
    rejectOpen = true
  }

  const confirmReject = async (rejection) => {
    const rejected = await rowAction(rejectTarget, 'reject', rejection, { undoable: true })
    if (rejected) rejectOpen = false
  }

  // -- Create Example submission ------------------------------------------------------------
  let pickerOpen = $state(false)
  let pickerTemplateId = $state('')
  let createOpen = $state(false)
  let createQuestId = $state(null)

  const openPicker = () => {
    pickerOpen = true
    pickerTemplateId = ''
  }

  const onTemplateSelected = (templateId) => {
    pickerTemplateId = templateId
    if (templateId) router.reload({ only: ['quest_occurrences'], data: { modal_quest_template_id: templateId } })
  }

  const pickQuest = (quest) => {
    pickerOpen = false
    createQuestId = quest.id
    createOpen = true
  }

  const formatWindow = (start, end) => {
    if (!start && !end) return '—'
    const s = start ? dayjs(start).format('MMM D, h:mma') : '?'
    const e = end ? dayjs(end).format('MMM D, h:mma') : '?'
    return `${s} → ${e}`
  }
</script>

<Layout>
  <PageHeader title="Quest Submissions" subtitle="Review and moderate user quest posts" count={pagy.count} total={total_count}>
    {#snippet actions()}
      {#if mode === 'pending' && pagy.count > 0}
        <Button variant="primary" size="sm" onclick={() => openReview()}>
          Start Reviewing ({pagy.count})
        </Button>
      {/if}
      <Button variant="default" size="sm" icon="add" onclick={openPicker}>Create Example submission</Button>
      <span title={undoTooltip}>
        <Button
          variant="default"
          size="sm"
          circle
          icon="undo"
          aria-label="Undo last review"
          disabled={!lastReview || undoBusy}
          onclick={undoLastReview}
        />
      </span>
    {/snippet}
  </PageHeader>

  <FilterBar>
    {#snippet start()}
      <FilterTabs
        param="mode"
        active={mode}
        tabs={[
          { key: 'pending', label: 'Pending' },
          { key: 'approved', label: 'Approved' },
          { key: 'rejected', label: 'Rejected' },
          { key: 'all', label: 'All' }
        ]}
      />
    {/snippet}
    {#snippet end()}
      <DisplayOptions entity="quest_submission" bind:viewMode />
    {/snippet}
  </FilterBar>

  <FilterBar>
    {#snippet start()}
      <SearchInput bind:value={search} placeholder="Search users..." />

      <FilterSelect
        label="Quest"
        value={filters.quest_template_id || ''}
        placeholder="All quests"
        source="quest_templates"
        sourceParams={{ area_id: $page.props.current_area_id }}
        onchange={(v) => applyFilters({ quest_template_id: v || undefined })}
      />

      <FilterSelect
        label="Media type"
        value={filters.media_type || ''}
        placeholder="Any media"
        options={MEDIA_TYPE_OPTIONS}
        onchange={(v) => applyFilters({ media_type: v || undefined })}
      />

      {#if mode === 'rejected'}
        <FilterSelect
          label="Rejection reason"
          value={filters.rejection_category || ''}
          placeholder="Any reason"
          options={rejection_categories}
          onchange={(v) => applyFilters({ rejection_category: v || undefined })}
        />
      {/if}

      {#if mode === 'approved' || mode === 'all'}
        <FilterSelect
          label="Rating"
          value={filters.rating || ''}
          placeholder="Any"
          options={rating_options}
          onchange={(v) => applyFilters({ rating: v || undefined })}
        />
      {/if}

      <FilterSelect
        label="Sort"
        value={filters.sort}
        placeholder={null}
        options={sortOptions}
        onchange={(v) => applyFilters({ sort: v })}
      />
    {/snippet}
  </FilterBar>

  {#if mode === 'pending' && viewMode !== 'board' && submissions.length > 0}
    <div class="flex items-center gap-3 p-3 mb-4 rounded-box bg-base-200">
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          class="checkbox checkbox-sm"
          checked={submissions.every((s) => selectedIds.has(s.id))}
          onchange={toggleSelectAll}
          title="Select all on this page"
        />
        <span class="text-sm">Select all on page</span>
      </label>
      {#if selectedIds.size > 0}
        <span class="font-semibold">{selectedIds.size} selected</span>
        <Button variant="primary" size="sm" disabled={bulkSubmitting} onclick={bulkApprove}>
          {bulkSubmitting ? 'Approving…' : `Approve ${selectedIds.size} selected`}
        </Button>
        <Button variant="ghost" size="sm" onclick={() => (selectedIds = new Set())}>Clear</Button>
      {/if}
    </div>
  {/if}

  <EntityList entity="quest_submission" rows={submissions} {viewMode} emptyMessage={empty_message}>
    {#snippet actions(submission)}
      {#if mode === 'pending'}
        <input
          type="checkbox"
          class="checkbox checkbox-sm"
          checked={selectedIds.has(submission.id)}
          onchange={() => toggleSelect(submission.id)}
          title="Select"
        />
      {/if}
      <Button variant="ghost" size="xs" onclick={() => openReview(submission)}>Review</Button>
      {#if submission.state === 'submitted'}
        <Button variant="success" size="xs" title="Approve" onclick={() => approve(submission)}>Approve</Button>
        <span title={submission.quest.has_example ? 'This quest already has an Example' : 'Approve & mark as Example — pinned #1 in the app'}>
          <Button variant="secondary" size="xs" disabled={submission.quest.has_example} onclick={() => approveExample(submission)}>
            +Example
          </Button>
        </span>
        <Button variant="error" size="xs" title="Reject — locks the moment private" onclick={() => openReject(submission)}>
          Reject
        </Button>
        <Button
          variant="error"
          outline
          size="xs"
          square
          icon="delete"
          title="Delete moment (TOS violation)"
          aria-label="Delete moment (TOS violation)"
          onclick={() => deleteMoment(submission)}
        />
      {:else if submission.state === 'approved'}
        <div class="join">
          {#each curation_levels as level (level.value)}
            {@const active = submission.curation_level === level.value}
            <Button
              variant={active ? 'warning' : 'ghost'}
              soft={active}
              size="xs"
              class="join-item"
              title={active ? 'Click again to clear the rating' : `Rate ${level.label} — ${level.definition}`}
              aria-pressed={active}
              onclick={() => rate(submission, level.value)}
            >
              {level.label}
            </Button>
          {/each}
        </div>
        {#if !submission.example}
          <span title={submission.quest.has_example ? 'This quest already has an Example — unmark it first' : 'Mark as Example — pinned #1 in the app'}>
            <Button
              variant="ghost"
              size="xs"
              square
              icon="sparkles"
              aria-label="Mark as Example"
              disabled={submission.quest.has_example}
              onclick={() => markExample(submission)}
            />
          </span>
        {/if}
      {/if}
    {/snippet}
  </EntityList>

  <Paginator {...pagy} />

  <RejectModal
    bind:open={rejectOpen}
    questPrompt={rejectTarget?.quest?.prompt}
    categories={rejection_categories}
    onconfirm={confirmReject}
  />

  <Modal bind:open={pickerOpen} title="Create Example submission" onclose={() => (pickerOpen = false)}>
    <p class="text-sm text-base-content/60 mb-4">
      Pick a quest template, then the generated occurrence to create the Example for. Occurrences that already have an
      Example are disabled.
    </p>

    <fieldset class="fieldset">
      <legend class="fieldset-legend">Quest template</legend>
      <Combobox
        value={pickerTemplateId}
        source="quest_templates"
        sourceParams={{ area_id: $page.props.current_area_id }}
        clearable
        placeholder="Select a quest template…"
        ariaLabel="Quest template"
        onchange={onTemplateSelected}
      />
    </fieldset>

    {#if pickerTemplateId}
      <div class="mt-4 flex flex-col gap-2">
        {#if (quest_occurrences.quests || []).length === 0}
          <p class="text-sm text-base-content/60">No generated quests for this template yet.</p>
        {/if}
        {#each quest_occurrences.quests || [] as quest (quest.id)}
          {@const disabledReason = quest.has_example ? 'Already has an Example' : null}
          <div class="flex items-center gap-3 p-3 border border-base-300 rounded-box" class:opacity-50={!!disabledReason}>
            <div class="flex-1">
              <div class="font-semibold text-sm">{quest.cycle_identifier}</div>
              <div class="text-xs text-base-content/60">{quest.prompt}</div>
              <div class="text-xs text-base-content/50 mt-1">
                Visible: {formatWindow(quest.visible_starts_at, quest.visible_ends_at)} · Active: {formatWindow(quest.active_starts_at, quest.active_ends_at)}
              </div>
            </div>
            {#if disabledReason}
              <span class="text-xs text-warning">{disabledReason}</span>
            {:else}
              <Button variant="secondary" outline size="sm" onclick={() => pickQuest(quest)}>Select</Button>
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    <div class="flex justify-end mt-4">
      <Button variant="ghost" onclick={() => (pickerOpen = false)}>Cancel</Button>
    </div>
  </Modal>

  <CreateExampleModal bind:open={createOpen} questId={createQuestId} reload={['submissions', 'pagy', 'empty_message']} />
</Layout>
